import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';
import { useAuth } from './auth/AuthContext';
import { backendSendPasswordReset, backendSendVerificationEmail } from './auth/backendAuth';
import { signInWithEmailPassword } from './auth/firebaseAuth';
import { friendlyError } from './utils/friendlyError';
import LoginScene from './components/LoginScene';

const LoginPage = () => {
  const navigate = useNavigate();
  const { user, authLoading, refreshUser, signOut } = useAuth();
  const emailInputRef = useRef(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const needsEmailVerification = useMemo(() => {
    if (!user) return false;
    return user.email && user.emailVerified === false;
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    if (user.email && user.emailVerified === false) return;
    navigate('/kpi-dashboard', { replace: true });
  }, [authLoading, navigate, user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    try {
      const signedInUser = await signInWithEmailPassword(formData.email, formData.password);

      if (signedInUser?.email && signedInUser.emailVerified === false) {
        await backendSendVerificationEmail();
        await signOut();
        setMessage('Verify your email first. We just sent you a verification email.');
        return;
      }

      navigate('/kpi-dashboard');
    } catch (err) {
      setError(friendlyError(err, 'Login failed. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    setError(null);
    setMessage(null);
    const email = formData.email?.trim();
    if (!email) {
      setError('Please enter your email, then click Forgot Password.');
      emailInputRef.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      await backendSendPasswordReset(email);
      setMessage('Password reset email sent. Check your inbox.');
    } catch (err) {
      setError(friendlyError(err, 'Could not send password reset email.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    setError(null);
    setMessage(null);
    setSubmitting(true);
    try {
      await backendSendVerificationEmail();
      setMessage('Verification email sent.');
    } catch (err) {
      setError(friendlyError(err, 'Could not send verification email.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleIHaveVerified = async () => {
    setError(null);
    setMessage(null);
    setSubmitting(true);
    try {
      const refreshedUser = await refreshUser();
      if (refreshedUser?.emailVerified) {
        navigate('/kpi-dashboard');
      } else {
        setMessage('Still not verified. If you just verified, wait a moment and try again.');
      }
    } catch (err) {
      setError(friendlyError(err, 'Could not refresh session.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-wrapper">
        <div className="animation-side">
          <LoginScene />

          {/* Brand overlay */}
          <div className="brand-overlay">
            <div className="brand-chip">
              <span className="brand-chip-dot" />
              Fleet Operations Platform
            </div>
            <h2 className="brand-hero">On the Move,<br />Every Mile.</h2>
            <p className="brand-sub">Real-time KPIs · Fuel &amp; Payroll · Source Control</p>
          </div>
        </div>

        <div className="form-side">
          <div className="login-form-container">
            <div className="form-header">
              {/* Brand mark */}
              <div className="form-brand-mark">
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="44" height="44" rx="12" fill="url(#bmGrad)" />
                  <defs>
                    <linearGradient id="bmGrad" x1="0" y1="0" x2="44" y2="44" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#1d4ed8" /><stop offset="1" stopColor="#3b82f6" />
                    </linearGradient>
                  </defs>
                  {/* Truck silhouette */}
                  <rect x="7" y="17" width="18" height="11" rx="1.5" fill="white" opacity="0.92" />
                  <rect x="25" y="19" width="10" height="9" rx="1.5" fill="white" opacity="0.92" />
                  <path d="M25 19 Q27 14 33 13 L36 13 Q37 14 37 17 L37 19Z" fill="white" opacity="0.7" />
                  <circle cx="13" cy="30" r="3" fill="white" opacity="0.85" />
                  <circle cx="22" cy="30" r="3" fill="white" opacity="0.85" />
                  <circle cx="32" cy="30" r="3" fill="white" opacity="0.85" />
                </svg>
              </div>
              <h1>Member Login</h1>
              <p>Access Your Exclusive Dashboard</p>
            </div>

            {authLoading ? (
              <div className="status-text">Checking session…</div>
            ) : needsEmailVerification ? (
              <div className="verify-panel">
                <div className="status-text">
                  Your email is not verified yet. Verify your inbox, then continue.
                </div>
                <div className="verify-actions">
                  <button
                    type="button"
                    className="login-button"
                    onClick={handleResendVerification}
                    disabled={submitting}
                  >
                    <span>Resend Verification Email</span>
                    <span className="arrow">→</span>
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={handleIHaveVerified}
                    disabled={submitting}
                  >
                    I verified my email
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="login-form">
                {message ? <div className="alert success">{message}</div> : null}
                {error ? <div className="alert error">{error}</div> : null}

                <div className="form-group">
                  <label htmlFor="email">
                    <i className="icon">✉️</i> Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                    autoComplete="email"
                    ref={emailInputRef}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    <i className="icon">🔒</i> Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                  />
                </div>

                <div className="form-options">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                    />
                    <span className="checkmark"></span>
                    Remember me
                  </label>
                  <button
                    type="button"
                    className="link-button"
                    onClick={handleForgotPassword}
                    disabled={submitting}
                  >
                    Forgot Password?
                  </button>
                </div>

                <button type="submit" className="login-button" disabled={submitting}>
                  <span>{submitting ? 'Signing in…' : 'Sign In'}</span>
                  <span className="arrow">→</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
